/**
 * CX-ADR-019 feature flags served from AWS AppConfig.
 *
 * This is the ADR-compliant flag tier: every flag is an object whose shape is
 * `{ enabled: boolean } & Record<string, unknown>` (flat, camelCase, nested at
 * most one level, noun-based names, no env prefix). It is deliberately separate
 * from the legacy env-baked `FeatureFlags` type in `./index.ts`, which predates
 * the ADR and uses bare booleans. New flags added under the ADR strategy belong
 * here; the two tiers coexist until the legacy flags are migrated.
 *
 * Flow:
 * - CDK provisions AppConfig from these values via `FeatureFlagConfig`
 *   (`@covestro-cx/cdk`), one profile per environment.
 * - A config-serving Lambda exposes them at `GET /v0/feature-flags`.
 * - The webapp reads them through `CovFeatureFlagProvider`
 *   (`@cx-component-library/react-components/feature-flags`) with
 *   {@link APP_CONFIG_FLAGS_FALLBACK} as the hard-coded fallback.
 *
 * No SDK/CDK imports here: this package is bundled into the browser webapp, so
 * it must stay dependency-free. The structural type below satisfies the
 * `FeatureFlagSchema` contract those packages declare without importing it.
 */

/**
 * The ADR-019 flag schema for Color Finder. Add new AppConfig-backed flags as
 * additional keys. Each value must have an `enabled` boolean and may carry
 * extra flag-specific properties.
 */
export interface AppConfigFeatureFlags {
  /**
   * Site-wide maintenance banner shown above the app shell. When `false`, no
   * banner renders — the deploy-gated default for every environment.
   * Ticket: CX-3168.
   * Remove when: a permanent notification system replaces the ad-hoc banner.
   */
  maintenanceBanner: {
    enabled: boolean;
  };
  /**
   * "What's New" release popup (CX-3596 / CX-3629). When `false`, the dialog is
   * never mounted for any user, regardless of release audience or per-user
   * seen-state — an environment-level kill-switch for the whole feature.
   * Ticket: CX-3629 (backend-integration follow-up; retag if a dedicated ticket
   * is cut).
   * Remove when: the What's New popup is a permanent part of the app and no
   * longer needs an environment-level off switch.
   */
  whatsNewDialog: {
    enabled: boolean;
  };
  /**
   * Original/cleaned data switch on the user settings page (CX-4014). When
   * `false` the switch is hidden and the webapp never sends `dataset=cleaned`;
   * the backend itself is not flag-gated, so this is the only kill-switch.
   * Ticket: CX-4014 (backend: CX-4013).
   * Remove when: the cleaned dataset replaces the original one, or the switch
   * becomes a permanent internal setting.
   */
  datasetSwitch: {
    enabled: boolean;
  };
  /**
   * Heart icon on the search-results grid tile that bookmarks a material into
   * a Customer Lounge collection (CX-3519). When `false` the heart is never
   * rendered; it is also hidden for anyone who is not a Customer Lounge user.
   * Ticket: CX-3519 (backend integration: CX-3786).
   * Remove when: collections ship in production and CX-3786 is live.
   */
  collectionBookmark: {
    enabled: boolean;
  };
  // Index signature keeps the type assignable to the shared `FeatureFlagSchema`
  // contract (`Record<string, { enabled: boolean } & Record<string, unknown>>`)
  // consumed by FeatureFlagConfig / CovFeatureFlagProvider.
  [key: string]: { enabled: boolean } & Record<string, unknown>;
}

/**
 * Fallback used by the webapp provider while the AppConfig fetch is in flight
 * and if it fails. Everything off so gated UI fails closed — matches the
 * CX-ADR-019 acceptance criterion "AppConfig fetch fails → hard-coded fallback".
 */
export const APP_CONFIG_FLAGS_FALLBACK: AppConfigFeatureFlags = {
  maintenanceBanner: { enabled: false },
  whatsNewDialog: { enabled: false },
  datasetSwitch: { enabled: false },
  collectionBookmark: { enabled: false },
};

/**
 * QA values: exercise the banner so QA can eyeball it before it ships to prod.
 * The What's New popup is on so QA can verify the release flow end-to-end, and
 * the dataset switch is on so QA can compare the original and cleaned data.
 * The collection heart is on so QA can exercise the mock-first bookmark flow.
 */
export const APP_CONFIG_FLAGS_QA: AppConfigFeatureFlags = {
  maintenanceBanner: { enabled: false },
  whatsNewDialog: { enabled: true },
  datasetSwitch: { enabled: true },
  collectionBookmark: { enabled: true },
};

/**
 * Production values: banner off. Flip via a follow-up PR + deploy when a real
 * maintenance window is scheduled. The dataset switch and the What's New popup
 * that announces it are on now that the first cleaned batch is imported in
 * production. The collection heart stays off until collections launch in
 * production.
 */
export const APP_CONFIG_FLAGS_PROD: AppConfigFeatureFlags = {
  maintenanceBanner: { enabled: false },
  whatsNewDialog: { enabled: true },
  datasetSwitch: { enabled: true },
  collectionBookmark: { enabled: false },
};
